/**
 * WebSocket Service - Real-time updates
 *
 * Merkezi WebSocket yonetimi - TUM platformlar icin
 * - Social Feed (post, like, comment)
 * - Habitat (agent response)
 * - Projects (proje guncelleme)
 * - Research (arastirma sonuc)
 * - RunCloud (kod degisiklik)
 * - FlzHub (topluluk etkinlik)
 */

export type WebSocketEventType =
  | 'new_post'
  | 'new_like'
  | 'new_comment'
  | 'new_share'
  | 'agent_response'
  | 'project_update'
  | 'project_updated'
  | 'research_result'
  | 'code_change'
  | 'community_event'
  | 'subscribed'
  | 'pong'
  | 'error';

export type WebSocketChannel =
  | 'social'
  | 'habitat'
  | 'projects'
  | 'research'
  | 'runcloud'
  | 'flzhub';

export interface ProjectUpdateData {
  project_id: number;
  event_type: 'finding_added' | 'question_asked' | 'message_sent' | 'agent_joined' | 'timeline_event';
  data: Record<string, unknown>;
}

export interface WebSocketEvent {
  type: WebSocketEventType;
  channel?: WebSocketChannel;
  data?: ProjectUpdateData | Record<string, unknown> | undefined;
  message?: string;
  timestamp?: string;
}

export type WebSocketEventHandler = (event: WebSocketEvent) => void;

class WebSocketService {
  private ws: WebSocket | null = null;
  private url: string;
  private userId: number | null = null;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 8;
  private readonly baseDelay = 1000;   // 1s
  private readonly maxDelay = 30000;  // 30s cap
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionalClose = false;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private handlers: Map<WebSocketEventType, Set<WebSocketEventHandler>> = new Map();
  private subscribedChannels: Set<WebSocketChannel> = new Set();

  constructor() {
    // WebSocket URL (ws:// for http://, wss:// for https://)
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname;
    const port = '8888'; // Backend port
    this.url = `${protocol}//${host}:${port}/ws`;
  }

  /**
   * Connect to WebSocket server
   */
  connect(userId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      this.userId = userId;
      this.intentionalClose = false;
      const wsUrl = `${this.url}?user_id=${userId}`;

      try {
        this.ws = new WebSocket(wsUrl);
        let settled = false;

        this.ws.onopen = () => {
          settled = true;
          this.reconnectAttempts = 0;
          this.startPing();
          this.subscribedChannels.forEach(ch => this._sendSubscribe(ch));
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data: WebSocketEvent = JSON.parse(event.data as string);
            this.handleMessage(data);
          } catch { /* parse error — ignore */ }
        };

        this.ws.onerror = () => {
          // onerror always precedes onclose — let onclose handle reconnect
          if (!settled) {
            settled = true;
            reject(new Error('WebSocket connection failed'));
          }
        };

        this.ws.onclose = () => {
          this.stopPing();
          if (!this.intentionalClose) {
            this.scheduleReconnect();
          }
        };

      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.intentionalClose = true;
    this.stopPing();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.reconnectAttempts = 0;
    this.subscribedChannels.clear();
  }

  /**
   * Subscribe to a channel
   */
  subscribe(channel: WebSocketChannel): void {
    this.subscribedChannels.add(channel);
    if (this.ws?.readyState === WebSocket.OPEN) {
      this._sendSubscribe(channel);
    }
    // Bağlı değilse subscribedChannels'a eklendi, reconnect sonrası gönderilir
  }

  private _sendSubscribe(channel: WebSocketChannel): void {
    this.send({ type: 'subscribe', channel });
  }

  /**
   * Unsubscribe from a channel
   */
  unsubscribe(channel: WebSocketChannel): void {
    this.subscribedChannels.delete(channel);
  }

  /**
   * Send message to server
   */
  private send(data: any): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[WS] Not connected, cannot send');
      return;
    }

    this.ws.send(JSON.stringify(data));
  }

  /**
   * Handle incoming message
   */
  private handleMessage(event: WebSocketEvent): void {
    const handlers = this.handlers.get(event.type);
    if (handlers) {
      handlers.forEach(handler => handler(event));
    }
  }

  /**
   * Register event handler
   */
  on(eventType: WebSocketEventType, handler: WebSocketEventHandler): () => void {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set());
    }
    const handlers = this.handlers.get(eventType);
    if (handlers) {
      handlers.add(handler);
    }

    // Return unsubscribe function
    return () => {
      this.handlers.get(eventType)?.delete(handler);
    };
  }

  /**
   * Remove event handler
   */
  off(eventType: WebSocketEventType, handler: WebSocketEventHandler): void {
    this.handlers.get(eventType)?.delete(handler);
  }

  /**
   * Start ping interval (keep-alive)
   */
  private startPing(): void {
    this.pingInterval = setInterval(() => {
      this.send({ type: 'ping' });
    }, 30000); // 30 seconds
  }

  /**
   * Stop ping interval
   */
  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Exponential backoff + jitter reconnect (international standard)
   * Delays: 1s, 2s, 4s, 8s, 16s, 30s, 30s, 30s
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn('[WS] Max reconnect attempts reached, giving up');
      return;
    }

    const exp = Math.pow(2, this.reconnectAttempts) * this.baseDelay;
    const jitter = Math.random() * 500;  // 0–500ms jitter
    const delay = Math.min(exp + jitter, this.maxDelay);

    this.reconnectAttempts++;
    console.log(`[WS] Reconnect #${this.reconnectAttempts} in ${Math.round(delay)}ms`);

    this.reconnectTimer = setTimeout(() => {
      if (this.userId && !this.intentionalClose) {
        this.connect(this.userId).catch(() => { /* scheduleReconnect onclose'ta tetiklenir */ });
      }
    }, delay);
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

// Singleton instance
export const websocketService = new WebSocketService();
